/**
 * Review bottom sheet + the inline "Leave a Review" button used on My Orders + Order Detail.
 *
 * Single review per order. Non-editable. Awards +25 FP on submit.
 */
import { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

export default function ReviewSheet({ orderId, onClose, onDone }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (rating === 0) return toast.error("Tap a star to rate");
    setBusy(true);
    try {
      await apiClient.post(`/marketplace/reviews/${orderId}`, {
        rating,
        review_text: text.trim().slice(0, 200),
      });
      toast.success("Review posted · +25 FitPoints");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-end"
      onClick={onClose}
      data-testid="review-sheet"
    >
      <div
        className="w-full bg-white rounded-t-[24px] p-5 max-w-2xl mx-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-barlow-cond font-extrabold text-[22px]">Leave a review</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 grid place-items-center rounded-full bg-canvas2"
          >
            <X size={14} />
          </button>
        </div>

        <div className="mt-4 flex justify-center gap-2" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              onClick={() => setRating(i)}
              aria-label={`${i} star`}
              data-testid={`star-${i}`}
              className="p-1"
            >
              <Star
                size={32}
                fill={i <= rating ? "#00D4FF" : "transparent"}
                color={i <= rating ? "#00D4FF" : "#A09C97"}
                strokeWidth={i <= rating ? 0 : 2}
              />
            </button>
          ))}
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 200))}
          placeholder="Tell others about this item..."
          data-testid="review-text"
          className="mt-4 w-full p-3 rounded-[12px] border border-line bg-canvas2 outline-none"
          style={{ minHeight: 90 }}
        />
        <div className="text-right text-[11px] text-ink-soft">{text.length}/200</div>

        <div className="flex gap-2.5 mt-3">
          <button
            onClick={onClose}
            data-testid="review-skip"
            className="flex-1 h-12 rounded-pill border border-line2 font-semibold"
            style={{ borderRadius: 100 }}
          >
            Skip
          </button>
          <button
            onClick={submit}
            disabled={busy || rating === 0}
            data-testid="review-submit"
            className="flex-1 h-12 rounded-pill disabled:opacity-50"
            style={{ background: "#00D4FF", color: "#000", borderRadius: 100, fontWeight: 700 }}
          >
            {busy ? "Posting…" : "Submit Review"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReviewButtonInline({ orderId, onReviewed }) {
  const [hasReviewed, setHasReviewed] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [sheet, setSheet] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/marketplace/reviews/order/${orderId}/mine`)
      .then((r) => setHasReviewed(Boolean(r.data?.review)))
      .catch(() => setHasReviewed(false));
  }, [orderId]);

  if (hasReviewed === null) return null;
  if (hasReviewed || dismissed) return null;

  return (
    <>
      <div className="mt-2 flex gap-2">
        <button
          onClick={() => setSheet(true)}
          data-testid={`leave-review-${orderId}`}
          className="flex-1 h-10 rounded-pill text-[13px] font-bold"
          style={{ background: "#00D4FF", color: "#000", borderRadius: 100 }}
        >
          Leave a Review
        </button>
        <button
          onClick={() => setDismissed(true)}
          data-testid={`skip-review-${orderId}`}
          className="px-4 h-10 rounded-pill text-[13px] font-semibold border border-line2"
          style={{ borderRadius: 100 }}
        >
          Skip
        </button>
      </div>
      {sheet && (
        <ReviewSheet
          orderId={orderId}
          onClose={() => setSheet(false)}
          onDone={() => {
            setHasReviewed(true);
            onReviewed?.();
          }}
        />
      )}
    </>
  );
}
